import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type SlotType = "member" | "breakout" | "guest";

export interface WeeklySpeakerRow {
  id: string;
  chapter: "washington" | "union";
  occurrence_date: string;
  member_id: string | null;
  topic: string | null;
  slot_type: SlotType;
  member?: {
    id: string;
    name: string;
    company: string;
    title: string | null;
    image: string | null;
  } | null;
}

export type SpeakerMap = Record<string, WeeklySpeakerRow>;

const keyOf = (chapter: string, date: string) => `${chapter}|${date}`;

export const useWeeklySpeakers = (chapter?: "washington" | "union" | "all") => {
  const [speakers, setSpeakers] = useState<SpeakerMap>({});
  const [loading, setLoading] = useState(true);

  const fetchSpeakers = useCallback(async () => {
    const today = new Date();
    const startDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

    let q = supabase
      .from("weekly_meeting_speakers")
      .select("id, chapter, occurrence_date, member_id, topic, slot_type")
      .gte("occurrence_date", startDate)
      .order("occurrence_date");

    if (chapter && chapter !== "all") {
      q = q.eq("chapter", chapter);
    }

    const { data, error } = await q;
    if (error) {
      console.error("Error loading weekly speakers:", error);
      setLoading(false);
      return;
    }

    const rows = (data || []) as WeeklySpeakerRow[];
    const memberIds = Array.from(
      new Set(rows.map((r) => r.member_id).filter((id): id is string => !!id))
    );
    let memberMap: Record<string, { id: string; name: string; company: string; title: string | null; image: string | null }> = {};
    if (memberIds.length > 0) {
      const { data: members } = await supabase
        .from("members")
        .select("id, name, company, title, image")
        .in("id", memberIds);
      (members || []).forEach((m: any) => {
        memberMap[m.id] = { id: m.id, name: m.name, company: m.company, title: m.title ?? null, image: m.image ?? null };
      });
    }

    const map: SpeakerMap = {};
    for (const r of rows) {
      map[keyOf(r.chapter, r.occurrence_date)] = {
        ...r,
        member: r.member_id ? memberMap[r.member_id] || null : null,
      };
    }
    setSpeakers(map);
    setLoading(false);
  }, [chapter]);

  useEffect(() => {
    fetchSpeakers();

    const channel = supabase
      .channel("weekly-speakers-changes")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "weekly_meeting_speakers" },
        () => fetchSpeakers()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchSpeakers]);

  return { speakers, loading, refetch: fetchSpeakers };
};

export const speakerKey = keyOf;

export interface ClaimArgs {
  chapter: "washington" | "union";
  occurrenceDate: string;
  memberId: string;
  topic?: string | null;
}

export const claimWeeklySlot = async ({ chapter, occurrenceDate, memberId, topic }: ClaimArgs) => {
  return supabase.from("weekly_meeting_speakers").insert({
    chapter,
    occurrence_date: occurrenceDate,
    member_id: memberId,
    topic: topic || null,
    slot_type: "member",
  });
};

export interface CreateNonMemberSlotArgs {
  chapter: "washington" | "union";
  occurrenceDate: string;
  slotType: "breakout" | "guest";
  topic?: string | null;
}

export const createNonMemberSlot = async ({
  chapter,
  occurrenceDate,
  slotType,
  topic,
}: CreateNonMemberSlotArgs) => {
  return supabase.from("weekly_meeting_speakers").insert({
    chapter,
    occurrence_date: occurrenceDate,
    member_id: null,
    topic: topic || null,
    slot_type: slotType,
  });
};

export const updateSpeakerTopic = async (id: string, topic: string | null) =>
  supabase.from("weekly_meeting_speakers").update({ topic }).eq("id", id);

export const releaseSpeakerSlot = async (id: string) =>
  supabase.from("weekly_meeting_speakers").delete().eq("id", id);
